import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import PageLayout from "../../components/PageLayout";
import Button from "../../components/Button";
import { getData, GET_VIRTUAL_GRADING_ITEM_DETAILS_URL } from "../../utils/api";
import UploadIcon from "../../icons/upload.svg";
import "./VirtualGrading.css";

// Mock data - in production this would come from API
const mockItem = {
  id: "item_1",
  title: "Toyota Catalytic Converter",
  make: "Toyota",
  model: "Camry",
  year: "2018",
  partNumber: "CAT123",
  description: "Used catalytic converter in good condition. Some minor rust but overall functional.",
  images: [
    "https://via.placeholder.com/600x400/cccccc/666666?text=",
    "https://via.placeholder.com/600x400/cccccc/666666?text=",
    "https://via.placeholder.com/600x400/cccccc/666666?text=",
  ],
  status: "graded",
  grade: "A",
  price: 120,
  adminComments: "Good condition, minimal rust. Excellent for resale.",
  submittedDate: "2024-10-20T10:30:00Z",
  gradedDate: "2024-10-21T14:00:00Z",
  userId: "user_456",
  userName: "John Doe",
  timeline: [
    {
      date: "2024-10-20T10:30:00Z",
      status: "submitted",
      message: "Item submitted for grading",
    },
    {
      date: "2024-10-21T14:00:00Z",
      status: "graded",
      message: "Item graded as A with price $120",
    },
  ],
};

const ItemDetail = () => {
  const { itemId } = useParams();
  const history = useHistory();
  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchItem = async () => {
      if (!itemId) return;
      
      setIsLoading(true);
      try {
        // API endpoint: GET /read/v1/service/grading/fetch/item?itemId=123
        const response = await getData(GET_VIRTUAL_GRADING_ITEM_DETAILS_URL, {
          itemId: itemId,
        });

        // API response has same keys as list API response
        // Map API response to component structure
        const itemData = response || {};
        
        // Build timeline from API data
        const timeline = [];
        
        // Add submitted event if createdDate exists
        if (itemData.createdDate) {
          timeline.push({
            date: new Date(itemData.createdDate).toISOString(),
            status: "submitted",
            message: "Item submitted for grading",
          });
        }

        // Add graded event if graded (status = 2)
        if (itemData.status === 2 || itemData.status === "graded") {
          if (itemData.gradedDate) {
            timeline.push({
              date: new Date(itemData.gradedDate).toISOString(),
              status: "graded",
              message: `Item graded as ${itemData.grade || "N/A"} with price $${itemData.price || itemData.assessedValue || itemData.gradePrice || 0}`,
            });
          }
        }

        // Add rejected event if rejected (status = 3)
        if (itemData.status === 3 || itemData.status === "rejected") {
          if (itemData.gradedDate) {
            timeline.push({
              date: new Date(itemData.gradedDate).toISOString(),
              status: "rejected",
              message: "Item rejected",
            });
          }
        }

        // Map API response to component structure (same keys as list API)
        const mappedItem = {
          id: itemData.itemId || itemData.id || itemId,
          itemId: itemData.itemId || itemData.id || itemId,
          title: itemData.title || "",
          make: itemData.make || "",
          model: itemData.model || "",
          year: itemData.year || "",
          partNumber: itemData.partNo || itemData.partNumber || "",
          description: itemData.description || "",
          images: itemData.itemURL ? [itemData.itemURL] : (itemData.images || []),
          status: itemData.status, // Keep as number (1=pending, 2=graded, 3=rejected)
          grade: itemData.grade || "",
          price: itemData.price || itemData.assessedValue || itemData.gradePrice || 0,
          adminNotes: itemData.adminNotes || itemData.adminComments || itemData.gradingNotes || itemData.notes || "",
          submittedDate: itemData.createdDate ? new Date(itemData.createdDate).toISOString() : null,
          gradedDate: itemData.gradedDate ? new Date(itemData.gradedDate).toISOString() : null,
          timeline: timeline,
        };

        setItem(mappedItem);
      } catch (error) {
        console.error("Error fetching item details:", error);
        setItem(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItem();
  }, [itemId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const statusStr = status === 1 ? "pending" : status === 2 ? "graded" : status === 3 ? "rejected" : status;
    switch (statusStr) {
      case "pending":
        return "#ffc107";
      case "graded":
        return "#28a745";
      case "rejected":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  if (isLoading) {
    return (
      <PageLayout 
        heading="Virtual Grading"
        icon={UploadIcon}
      >
        <div className="virtual-grading-container">
          <div className="vg-item-detail-content">
            <div className="vg-loading">Loading item details...</div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!item) {
    return (
      <PageLayout 
        heading="Virtual Grading"
        icon={UploadIcon}
      >
        <div className="virtual-grading-container">
          <div className="vg-item-detail-content">
            <div className="vg-header-top">
              <Button
                className="vg-back-button"
                onClick={() => history.goBack()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                  className="mr-8"
                >
                  <path
                    fillRule="evenodd"
                    d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                  />
                </svg>
                Back
              </Button>
            </div>
            <div className="vg-error">Item not found</div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      heading="Virtual Grading"
      icon={UploadIcon}
    >
      <div className="virtual-grading-container">
        <div className="vg-item-detail-content">
          <div className="vg-header-top">
            <Button
              className="vg-back-button"
              onClick={() => history.goBack()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 16 16"
                className="mr-8"
              >
                <path
                  fillRule="evenodd"
                  d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
                />
              </svg>
              Back
            </Button>
          </div>
          <div className="vg-item-detail-main">
            {/* Left Column: Image and Timeline */}
            <div className="vg-left-column">
              {/* Single Image */}
              <div className="vg-single-image">
                <img
                  src={item.images && item.images.length > 0 ? item.images[0] : item.itemURL || "https://via.placeholder.com/600x400"}
                  alt={item.title || "Item"}
                />
              </div>

              {/* Timeline */}
              <div className="vg-timeline-section">
                <h4 className="vg-section-title">Timeline</h4>
                <div className="vg-timeline">
                  {item.timeline.map((event, index) => (
                    <div key={index} className="vg-timeline-item">
                      <div className="vg-timeline-dot"></div>
                      <div className="vg-timeline-content">
                        <div className="vg-timeline-date">
                          {formatDate(event.date)}
                        </div>
                        <div className="vg-timeline-message">
                          {event.message}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column: Item Information */}
            <div className="vg-item-info-section">
              <div className="vg-item-header">
                <h3 className="vg-item-title">{item.title || "Untitled Item"}</h3>
                <div className="vg-item-status">
                  <span
                    className="vg-status-badge"
                    style={{ backgroundColor: getStatusColor(item.status) }}
                  >
                    {(typeof item.status === 'string' ? item.status : (item.status === 1 ? "pending" : item.status === 2 ? "graded" : item.status === 3 ? "rejected" : "pending")).toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="vg-item-details">
                <div className="vg-detail-row">
                  <span className="vg-detail-label">Make:</span> <span className="vg-detail-value">{item.make || "N/A"}</span>
                </div>
                <div className="vg-detail-row">
                  <span className="vg-detail-label">Model:</span> <span className="vg-detail-value">{item.model || "N/A"}</span>
                </div>
                <div className="vg-detail-row">
                  <span className="vg-detail-label">Year:</span> <span className="vg-detail-value">{item.year || "N/A"}</span>
                </div>
                <div className="vg-detail-row">
                  <span className="vg-detail-label">Part Number:</span> <span className="vg-detail-value">{item.partNumber || item.partNo || "N/A"}</span>
                </div>
                {item.submittedDate && (
                  <div className="vg-detail-row">
                    <span className="vg-detail-label">Submitted:</span> <span className="vg-detail-value">{formatDate(item.submittedDate)}</span>
                  </div>
                )}
                {(item.status === "graded" || item.status === 2) && (
                  <>
                    {item.grade && (
                      <div className="vg-detail-row">
                        <span className="vg-detail-label">Grade:</span> <span className="vg-detail-value vg-grade-value">
                          {item.grade}
                        </span>
                      </div>
                    )}
                    {(item.price || item.assessedValue || item.gradePrice) && (
                      <div className="vg-detail-row">
                        <span className="vg-detail-label">Price:</span> <span className="vg-detail-value vg-price-value">
                          ${item.price || item.assessedValue || item.gradePrice || 0}
                        </span>
                      </div>
                    )}
                    {item.gradedDate && (
                      <div className="vg-detail-row">
                        <span className="vg-detail-label">Graded:</span> <span className="vg-detail-value">{formatDate(item.gradedDate)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Description - Always shown */}
              <div className="vg-description-section">
                <h4 className="vg-section-title">Description</h4>
                <p className="vg-description">{item.description || "N/A"}</p>
              </div>

              {/* Admin Notes - Always shown */}
              <div className="vg-admin-comments-section">
                <h4 className="vg-section-title">Admin Notes</h4>
                <p className="vg-admin-comments">{item.adminNotes || item.adminComments || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ItemDetail;
